import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import DashboardIcon from '../../icons/DashboardIcon';
import UserIcon from '../../icons/UserIcon'
import MeetingIcon from '../../icons/MeetingIcon';
import LeadsIcon from '../../icons/LeadsIcon';
import ClientsIcon from '../../icons/ClientsIcon';
import ReportsIcon from '../../icons/ReportsIcon';

import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import CelebrationIcon from '@mui/icons-material/Celebration';
import BadgeIcon from '@mui/icons-material/Badge';
import PhoneIcon from '@mui/icons-material/Phone';

import TaskIcon from '../../icons/TaskIcon';
import ApiService from '../../../services/api';
import { ROLES, ROLE_PERMISSIONS as CONTEXT_PERMISSIONS } from '../../../contexts/AdminRoleContext';

// Use centralized permissions from AdminRoleContext
const ROLE_PERMISSIONS = CONTEXT_PERMISSIONS;

// Styled components
const SidebarContainer = styled(Box)(({ theme }) => ({
  width: '206px',
  backgroundColor: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: theme.spacing(2.5),
  boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
  borderRadius: theme.spacing(0, 1.5, 1.5, 0)
}));

const TopItemsSection = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1.5),
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5)
}));

const NavItem = styled(Box)(({ theme, isactive }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(0.5),
  cursor: 'pointer',
  backgroundColor: isactive === 'true' ? 'rgba(11, 145, 214, 0.10)' : 'transparent',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: isactive === 'true' ? 'rgba(11, 145, 214, 0.10)' : theme.palette.grey[50],
    transform: 'translateX(2px)'
  }
}));

const DashboardNavItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '0px',
  cursor: 'pointer'
});

const DashboardContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(0.5),
  backgroundColor: 'rgba(11, 145, 214, 0.10)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(11, 145, 214, 0.15)',
    transform: 'translateX(2px)'
  }
}));

const DashboardUnderline = styled(Box)(({ theme }) => ({
  height: '2px',
  backgroundColor: '#0b91d6',
  marginLeft: theme.spacing(2.5),
  marginRight: theme.spacing(2.5)
}));

const NavText = styled(Typography)(({ theme, isactive }) => ({
  fontSize: '16px',
  fontWeight: isactive === 'true' ? 600 : 500,
  color: isactive === 'true' ? '#0b91d6' : '#121927',
  transition: 'color 0.2s ease-in-out'
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.2s ease-in-out'
}));

const AdminDashboardSidebar = ({ onViewChange }) => {
  const [activeItem, setActiveItem] = useState('dashboard');
  const [roleId, setRoleId] = useState(null); // No default role for security
  const navigate = useNavigate();

  // Fetch employee role on component mount
  useEffect(() => {
    const fetchEmployeeRole = async () => {
      try {
        // Get user ID and role from localStorage
        let userId = null;
        let storedRoleId = null;

        // Check userData first (stored by login form)
        const userDataRaw = localStorage.getItem('userData');
        if (userDataRaw) {
          try {
            const userData = JSON.parse(userDataRaw);
            userId = userData?.id || userData?.user_id;
            storedRoleId = userData?.role_id || userData?.employee?.role_id;
          } catch (e) { }
        }

        // Fallback to auth
        if (!userId) {
          const authRaw = localStorage.getItem('auth');
          if (authRaw) {
            const auth = JSON.parse(authRaw);
            userId = auth?.id || auth?.user?.id || auth?.data?.id;
            storedRoleId = storedRoleId || auth?.role_id || auth?.user?.role_id;
          }
        }

        // Fallback to user
        if (!userId) {
          const userRaw = localStorage.getItem('user');
          if (userRaw) {
            const user = JSON.parse(userRaw);
            userId = user?.id;
            storedRoleId = storedRoleId || user?.role_id;
          }
        }

        // If we got role_id from stored data, use it (role 9 is the new admin role)
        if (storedRoleId) {
          setRoleId(Number(storedRoleId));
          return;
        }

        // Fallback: fetch from employee API
        if (userId) {
          try {
            const response = await ApiService.get(`/admin/employee/get-employee-by-user/${userId}`);
            const employee = response?.data?.data || response?.data || response;
            if (employee && employee.role_id) {
              setRoleId(employee.role_id);
            }
          } catch (err) {
            // If endpoint fails, keep Super Admin (default for old admins)
            console.log('Employee role not found, defaulting to Super Admin');
          }
        }
      } catch (error) {
        console.error('Error fetching employee role:', error);
      }
    };

    fetchEmployeeRole();
  }, []);

  // Check if the current role can access a specific menu item
  const canAccess = (menuItemId) => {
    const permissions = ROLE_PERMISSIONS[roleId];
    if (permissions === 'all') return true;
    if (Array.isArray(permissions)) return permissions.includes(menuItemId);
    // If no permissions defined for this role, default to hidden for security
    return false;
  };

  const handleItemClick = (item) => {
    setActiveItem(item.id);
    if (onViewChange) {
      onViewChange(item.id);
    }
    // Always clear any nested routes like '/employee-view/:id' by navigating
    // to a clean admin dashboard route when switching sections
    navigate(item.path || '/admin-dashboard');
  };

  const allMenuItems = [
    { id: 'users', label: 'Users', icon: UserIcon, path: '/admin-dashboard/users' },
    { id: 'employee', label: 'Employees', icon: BadgeIcon, path: '/admin-dashboard/employee' },
    { id: 'leads', label: 'Leads', icon: LeadsIcon, path: '/admin-dashboard/leads' },
    { id: 'meeting', label: 'Meetings', icon: MeetingIcon, path: '/admin-dashboard/meetings' },
    { id: 'clients', label: 'Clients', icon: ClientsIcon, path: '/admin-dashboard/clients' },
    { id: 'payments', label: 'Payments', icon: AccountBalanceWalletIcon, path: '/admin-dashboard/payments' },
    { id: 'engagement', label: 'Engagement', icon: TrendingUpIcon, path: '/admin-dashboard/engagement' },
    { id: 'festivals', label: 'Festivals', icon: CelebrationIcon, path: '/admin-dashboard/festivals' },

    { id: 'photos', label: 'Photos', icon: PhotoLibraryIcon, path: '/admin-dashboard/photos' },
    { id: 'tasks', label: 'Tasks', icon: TaskIcon, path: '/admin-dashboard/tasks' },
    { id: 'holidays', label: 'Holidays', icon: BeachAccessIcon, path: '/admin-dashboard/holidays' },
    { id: 'gmbs', label: 'GMB Plans', icon: StorefrontIcon, path: '/admin-dashboard/gmb-manager' },
    { id: 'gmbRequests', label: 'GMB Requests', icon: PhoneIcon, path: '/admin-dashboard/gmb-requests' },
  ];

  // Filter menu items based on role permissions
  // Admin Management is only visible to Super Admins
  const topMenuItems = allMenuItems.filter(item => {
    if (item.superAdminOnly && roleId !== ROLES.SUPER_ADMIN) return false;
    return canAccess(item.id) || item.superAdminOnly;
  });

  // Check if user can access dashboard (Admin with role_id 9 only)
  const canAccessDashboard = roleId === 9;

  return (
    <SidebarContainer>
      <TopItemsSection>
        {/* Dashboard visible only for Admins (not employees with role_id 4) */}
        {canAccessDashboard && (
          <DashboardNavItem onClick={() => handleItemClick({ id: 'dashboard', path: '/admin-dashboard' })}>
            <DashboardContent>
              <IconWrapper>
                <DashboardIcon width={15} height={17} color="#0b91d6" />
              </IconWrapper>
              <NavText isactive="true">Dashboard</NavText>
            </DashboardContent>
            <DashboardUnderline />
          </DashboardNavItem>
        )}

        {topMenuItems.map((item) => {
          const IconComponent = item.icon;
          const isActive = activeItem === item.id;

          return (
            <NavItem
              key={item.id}
              isactive={isActive.toString()}
              onClick={() => handleItemClick(item)}
            >
              <IconWrapper>
                <IconComponent
                  width={
                    item.id === 'demo-account' ? 18 :
                      item.id === 'reports' ? 15 : 20
                  }
                  height={
                    item.id === 'demo-account' ? 16 :
                      item.id === 'reports' ? 16 : 20
                  }
                  color={
                    isActive ? '#0b91d6' : '#121927'
                  }
                />
              </IconWrapper>
              <NavText isactive={isActive.toString()}>{item.label}</NavText>
            </NavItem>
          );
        })}
      </TopItemsSection>
    </SidebarContainer>
  );
};

export default AdminDashboardSidebar;
